import type { StepId as ModelStepId } from "@redotech/redo-return-core-common/return-flow-answers";
import type { ExtendedModelStep } from "@redotech/redo-return-ui/flows/model-to-steps";
import { Step } from "@redotech/redo-return-ui/flows/step";

const STUB_MODEL_STEP_ID: ModelStepId = 0;

/**
 * Convert an editor step to its persisted model shape, narrowed to the
 * discriminated `ExtendedModelStep` union so callers can switch on
 * `model.type` without casting.
 *
 * Returns `undefined` if `toModel` throws. Partially-configured drafts
 * (e.g. a fresh Condition without a `conditionType`, a Compensation step
 * with no methods selected) intentionally throw out of their `toModel`
 * implementations. Predicates that just need the discriminator (the
 * wiring validator, phantom-edge layout, node rendering) should treat
 * that as "shape unknown" rather than crash.
 *
 * **This is a workaround.** The cleaner fix is to give every `StepType`
 * a `toModel()` that returns `Model | undefined` (or a `Result` shape)
 * instead of throwing — but that change ripples across every step type
 * in `redo/return/ui/src/flows/return-flow/` (60+ files), so it's
 * deferred to a focused PR. Until then, this helper is the only place
 * that's allowed to swallow `toModel` exceptions; everywhere else should
 * still propagate them.
 *
 * The id-mapper is stubbed because predicates only inspect `model.type`
 * (and any non-id fields like `phase`), never the persisted edge ids.
 */
export function safeToModel(step: Step): ExtendedModelStep | undefined {
  try {
    return step.type.toModel(step.state, () => STUB_MODEL_STEP_ID);
  } catch {
    return undefined;
  }
}
